import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import {
  Shift,
  ShiftTable,
  Workplace,
  WorkplaceUser,
} from "../components/ShiftTable";
import {
  GetWorkplaceUsersByWorkplaceIdDocument,
  SelectShiftsByWorkplaceIdAndYearAndMonthAndStartDayDocument,
} from "../gql/graphql";
import html2canvas from "html2canvas";
import { ShiftHeader } from "../components/ShiftHeader";
import { Loading } from "../components/Loading";
import { ErrorMessage } from "../components/ErrorMessage";

const currentDate = new Date();

type ShiftPageProps = {};

export const ShiftPage: React.FC<ShiftPageProps> = (props) => {
  const { workplaceId } = useParams();
  const [searchParams] = useSearchParams();

  const [year, month] = useMemo(() => {
    const _year = searchParams.get("year")
      ? parseInt(searchParams.get("year")!)
      : currentDate.getFullYear();

    const _month = searchParams.get("month")
      ? parseInt(searchParams.get("month")!)
      : currentDate.getMonth() + 1;

    const date = new Date(_year, _month - 1);

    return [date.getFullYear(), date.getMonth() + 1];
  }, [searchParams]);

  const navigate = useNavigate();

  const [shiftsState, getShifts] = useMutation(
    SelectShiftsByWorkplaceIdAndYearAndMonthAndStartDayDocument
  );

  const [workplaceUsersState] = useQuery({
    query: GetWorkplaceUsersByWorkplaceIdDocument,
    variables: { workplaceId: workplaceId },
    requestPolicy: "cache-first",
  });

  const handleNavigateNextMonth = useCallback(() => {
    navigate(`?year=${year}&month=${month + 1}`, { replace: true });
  }, [month, navigate, year]);

  const handleNavigatePrevMonth = useCallback(() => {
    navigate(`?year=${year}&month=${month - 1}`, { replace: true });
  }, [month, navigate, year]);

  const handleNavigateToday = useCallback(() => {
    const today = new Date();
    navigate(`?year=${today.getFullYear()}&month=${today.getMonth() + 1}`, {
      replace: true,
    });
  }, [navigate]);

  useEffect(() => {
    getShifts(
      {
        input: {
          _month: month,
          _startDay: 1,
          _year: year,
          _workplaceId: workplaceId,
        },
      },
      { requestPolicy: "cache-first" }
    );

    navigate(`?year=${year}&month=${month}`, { replace: true });
  }, [month, year, workplaceId, getShifts, navigate]);

  const workplaceUsers: WorkplaceUser[] =
    workplaceUsersState.data?.workplaceById?.workplaceUsersByWorkplaceId.edges
      .sort((a, b) => a.node.order - b.node.order)
      .map((e) => {
        return {
          id: e.node.id,
          name: e.node.name,
          order: e.node.order,
        };
      }) ?? [];

  const workplace: Workplace | undefined = workplaceUsersState.data
    ?.workplaceById
    ? {
        name: workplaceUsersState.data?.workplaceById.name,
      }
    : undefined;

  const shifts: Shift[] =
    shiftsState.data?.selectShiftsByWorkplaceIdAndYearAndMonthAndStartDay?.shifts?.map(
      (e) => {
        // shiftSettingまたはcustomShiftを取得（片方しか存在しない）
        const setting = e.shiftSettingByShiftSettingId ?? e.customShiftByCustomShiftId;
        return {
          workplaceUserId: e.workplaceUserId,
          name: setting?.name ?? "",
          hexColor: setting?.hexColor ?? "",
          date: new Date(e.date),
        };
      }
    ) ?? [];

  const shiftBoxId = "shift-box";

  // ローディング状態のチェック
  const isLoading = shiftsState.fetching || workplaceUsersState.fetching;

  // エラー状態のチェック
  const hasError = shiftsState.error || workplaceUsersState.error;

  // リトライ関数
  const handleRetry = useCallback(() => {
    getShifts(
      {
        input: {
          _month: month,
          _startDay: 1,
          _year: year,
          _workplaceId: workplaceId,
        },
      },
      { requestPolicy: "network-only" }
    );
  }, [month, year, workplaceId, getShifts]);

  return (
    <div
      id={shiftBoxId}
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100vh",
        backgroundColor: "#ffffff",
      }}
    >
      <ShiftHeader
        year={year}
        month={month}
        workplaceName={workplace?.name}
        onPrevMonth={handleNavigatePrevMonth}
        onNextMonth={handleNavigateNextMonth}
        onToday={handleNavigateToday}
        onDownload={() =>
          downloadShift({
            elementId: shiftBoxId,
            workplaceName: workplace?.name || "",
            year: year,
            month: month,
          })
        }
      />
      <div style={{ flex: 1, overflow: "hidden" }}>
        {isLoading ? (
          <Loading message="シフトを読み込んでいます..." />
        ) : hasError ? (
          <ErrorMessage
            title="データの読み込みに失敗しました"
            message={
              shiftsState.error?.message ||
              workplaceUsersState.error?.message ||
              "予期しないエラーが発生しました。再度お試しください。"
            }
            onRetry={handleRetry}
          />
        ) : (
          <ShiftTable
            year={year}
            month={month}
            shifts={shifts}
            workplaceUsers={workplaceUsers}
          />
        )}
      </div>
    </div>
  );
};

type downloadShiftArgs = {
  elementId: string;
  workplaceName: string;
  year: number;
  month: number;
};

const downloadShift = (args: downloadShiftArgs) => {
  html2canvas(document.getElementById(args.elementId)!).then(function (canvas) {
    const downloadEle = document.createElement("a");
    downloadEle.href = canvas.toDataURL("image/png");
    downloadEle.download = `${args.workplaceName}-${args.year}-${args.month}.png`;
    downloadEle.click();
  });
};
